import React, { useEffect, useRef } from 'react';
import { Editor } from '@tiptap/react';
import { ArrowDown } from '@producer/ui-kit';

/*
 * The commands this toolbar runs are declared by the extensions the shared editor registers, so
 * their types only reach this file if it pulls the extension modules in itself.
 */
import type {} from '@tiptap/starter-kit';
import type {} from '@tiptap/extension-highlight';

import { DropdownMenu } from '../modals/dropdown-menu/DropdownMenu';

import styles from './editor-bubble-menu.module.scss';

export const EditorBubbleMenu: React.FC<{
  editor: Editor;
}> = ({ editor }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (menuRef.current) {
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          menuRef.current?.style.setProperty('display', 'none');
        }
      });

      const domSelection = window.getSelection();
      if (!domSelection) return;

      const domRange = domSelection.getRangeAt(0);
      const rect = domRange.getBoundingClientRect();

      const editorElement = editor.view.dom as HTMLElement;
      const editorRect = editorElement.getBoundingClientRect();

      const top = rect.top - 40;

      const menuWidth = menuRef.current.offsetWidth;
      let left = rect.left - menuWidth / 2 + rect.width / 2;

      if (left < editorRect.left) {
        left = editorRect.left;
      }

      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10;
      }

      menuRef.current.style.top = `${top}px`;
      menuRef.current.style.left = `${left}px`;
    }
  }, [editor]);

  const handleHeadingSelect = (value: number) => {
    editor
      .chain()
      .focus()
      .toggleHeading({ level: value as 1 | 2 | 3 | 4 | 5 | 6 })
      .run();
  };

  return (
    <div className={styles['custom-bubble-menu']} ref={menuRef}>
      <button
        className={styles['custom-icon-button']}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        disabled={!editor.can().chain().focus().toggleHighlight().run()}
        className={editor.isActive('highlight') ? 'is-active' : ''}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.9797 0.246544C11.175 0.0512821 11.4916 0.0512821 11.6868 0.246544L13.8202 2.37988C14.0154 2.57514 14.0154 2.89172 13.8202 3.08698L9.61086 7.29628C9.53486 7.37228 9.44711 7.43553 9.35097 7.4836L5.15688 9.58064C4.96438 9.67689 4.7319 9.63916 4.57972 9.48698C4.42754 9.3348 4.38981 9.10232 4.48606 8.90982L6.5831 4.71573C6.63117 4.6196 6.69442 4.53184 6.77042 4.45584L10.9797 0.246544ZM11.3333 1.3072L7.47753 5.16294L6.37262 7.37276L6.69394 7.69408L8.90376 6.58917L12.7595 2.73343L11.3333 1.3072ZM8.63327 1.16676L7.63327 2.16676H3.06663C2.63834 2.16676 2.34719 2.16715 2.12215 2.18554C1.90294 2.20345 1.79084 2.23591 1.71264 2.27576C1.52447 2.37163 1.37149 2.52461 1.27562 2.71277C1.23578 2.79097 1.20331 2.90307 1.1854 3.12228C1.16702 3.34733 1.16663 3.63847 1.16663 4.06676V11.0001C1.16663 11.4284 1.16702 11.7195 1.1854 11.9446C1.20331 12.1638 1.23578 12.2759 1.27562 12.3541C1.37149 12.5422 1.52447 12.6952 1.71264 12.7911C1.79084 12.8309 1.90294 12.8634 2.12215 12.8813C2.34719 12.8997 2.63834 12.9001 3.06663 12.9001H9.99996C10.4282 12.9001 10.7194 12.8997 10.9444 12.8813C11.1636 12.8634 11.2758 12.8309 11.354 12.7911C11.5421 12.6952 11.6951 12.5422 11.791 12.3541C11.8308 12.2759 11.8633 12.1638 11.8812 11.9446C11.8996 11.7195 11.9 11.4284 11.9 11.0001V6.43341L12.9 5.43341V11.0001V11.0208C12.9 11.4232 12.9 11.7554 12.8779 12.026C12.8549 12.3071 12.8056 12.5654 12.682 12.8081C12.4902 13.1844 12.1843 13.4904 11.8079 13.6821C11.5653 13.8058 11.3069 13.855 11.0259 13.878C10.7553 13.9001 10.4231 13.9001 10.0206 13.9001H9.99996H3.06663H3.04596C2.64349 13.9001 2.31131 13.9001 2.04071 13.878C1.75967 13.855 1.50131 13.8058 1.25865 13.6821C0.882322 13.4904 0.576361 13.1844 0.384614 12.8081C0.260968 12.5654 0.211686 12.3071 0.188724 12.026C0.166615 11.7554 0.16662 11.4232 0.166626 11.0208V11.0208V11.0207V11.0001V4.06676V4.04611V4.0461V4.04609C0.16662 3.64362 0.166615 3.31144 0.188724 3.04085C0.211686 2.7598 0.260968 2.50145 0.384614 2.25878C0.576361 1.88246 0.882322 1.5765 1.25865 1.38475C1.50131 1.2611 1.75967 1.21182 2.04071 1.18886C2.31131 1.16675 2.64349 1.16676 3.04597 1.16676H3.04598H3.06663H8.63327Z"
            fill="#7A8296"
          />
        </svg>
      </button>

      <button
        className={styles['custom-icon-button']}
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
      >
        <svg
          width="9"
          height="10"
          viewBox="0 0 9 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.44544 9.79995C1.02198 9.79995 0.718565 9.70609 0.535209 9.51837C0.356219 9.32629 0.266724 9.02287 0.266724 8.60814V1.39177C0.266724 0.968301 0.358402 0.66489 0.541758 0.481534C0.729479 0.293812 1.03071 0.199951 1.44544 0.199951C2.85596 0.199951 4.80636 0.199952 5.63048 0.199952C6.81321 0.199952 7.79087 1.24692 7.79087 2.53019C7.79087 3.49063 7.31065 4.27308 6.35021 4.71837C7.59016 4.94503 8.28553 6.20302 8.28553 7.11332C8.28553 8.21327 7.72291 9.79995 5.89589 9.79995C5.36263 9.79995 2.85473 9.79995 1.44544 9.79995ZM4.93756 5.53692H2.18672V8.33965H4.93756C5.49339 8.33965 6.27163 7.7958 6.27163 6.91864C6.27163 6.04148 5.49339 5.53692 4.93756 5.53692ZM2.18672 1.66025V4.14211H4.72506C5.10839 4.14211 5.89589 3.7833 5.89589 2.86663C5.89589 1.94997 5.04589 1.66025 4.72506 1.66025H2.18672Z"
            fill="#7A8296"
          />
        </svg>
      </button>

      <button
        className={styles['custom-icon-button']}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
      >
        <svg
          width="8"
          height="10"
          viewBox="0 0 8 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.05333 0.733418C2.05333 0.468321 2.26823 0.253418 2.53333 0.253418H7.33333C7.59843 0.253418 7.81333 0.468321 7.81333 0.733418C7.81333 0.998515 7.59843 1.21342 7.33333 1.21342H5.60632L3.71302 8.78662H5.46665C5.73174 8.78662 5.94665 9.00152 5.94665 9.26662C5.94665 9.53172 5.73174 9.74662 5.46665 9.74662H0.666646C0.401549 9.74662 0.186646 9.53172 0.186646 9.26662C0.186646 9.00152 0.401549 8.78662 0.666646 8.78662H2.39363L4.28693 1.21342H2.53333C2.26823 1.21342 2.05333 0.998515 2.05333 0.733418Z"
            fill="#7A8296"
          />
        </svg>
      </button>

      <button
        className={styles['custom-icon-button']}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'is-active' : ''}
      >
        <svg
          width="12"
          height="11"
          viewBox="0 0 12 11"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.29999 1.46655C3.29999 1.19041 3.07614 0.966553 2.79999 0.966553C2.52385 0.966553 2.29999 1.19041 2.29999 1.46655V5.59992H0.666602C0.445688 5.59992 0.266602 5.779 0.266602 5.99992C0.266602 6.22083 0.445688 6.39992 0.666602 6.39992H2.29999V7.11989C2.29999 9.16334 3.95654 10.8199 5.99999 10.8199C8.04345 10.8199 9.69999 9.16334 9.69999 7.11989V6.39992H11.3333C11.5542 6.39992 11.7333 6.22083 11.7333 5.99992C11.7333 5.779 11.5542 5.59992 11.3333 5.59992H9.69999V1.46655C9.69999 1.19041 9.47614 0.966553 9.19999 0.966553C8.92385 0.966553 8.69999 1.19041 8.69999 1.46655V5.59992H3.29999V1.46655ZM3.29999 6.39992V7.11989C3.29999 8.61106 4.50883 9.81989 5.99999 9.81989C7.49116 9.81989 8.69999 8.61106 8.69999 7.11989V6.39992H3.29999Z"
            fill="#7A8296"
          />
        </svg>
      </button>

      <button
        className={styles['custom-icon-button']}
        onClick={() => editor.chain().focus().toggleCode().run()}
        disabled={!editor.can().chain().focus().toggleCode().run()}
        className={editor.isActive('code') ? 'is-active' : ''}
      >
        <svg
          width="14"
          height="12"
          viewBox="0 0 14 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.59755 0.852332C9.7001 0.595941 9.5754 0.304955 9.319 0.202399C9.06261 0.0998419 8.77163 0.22455 8.66907 0.480942L4.4024 11.1476C4.29985 11.404 4.42456 11.695 4.68095 11.7975C4.93734 11.9001 5.22832 11.7754 5.33088 11.519L9.59755 0.852332ZM3.08686 3.51308C3.28212 3.70835 3.28212 4.02493 3.08686 4.22019L1.30708 5.99997L3.08686 7.77975C3.28212 7.97501 3.28212 8.29159 3.08686 8.48686C2.8916 8.68212 2.57502 8.68212 2.37976 8.48686L0.246422 6.35352C0.05116 6.15826 0.05116 5.84168 0.246422 5.64642L2.37976 3.51308C2.57502 3.31782 2.8916 3.31782 3.08686 3.51308ZM10.9131 3.51308C11.1084 3.31782 11.4249 3.31782 11.6202 3.51308L13.7535 5.64642C13.9488 5.84168 13.9488 6.15826 13.7535 6.35352L11.6202 8.48686C11.4249 8.68212 11.1084 8.68212 10.9131 8.48686C10.7178 8.29159 10.7178 7.97501 10.9131 7.77975L12.6929 5.99997L10.9131 4.22019C10.7178 4.02493 10.7178 3.70835 10.9131 3.51308Z"
            fill="#7A8296"
          />
        </svg>
      </button>

      <button
        className={styles['custom-icon-button']}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
      >
        <svg
          fill="#000000"
          width="800px"
          height="800px"
          viewBox="0 0 256 256"
          id="Flat"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M56,128a12,12,0,1,1-12-12A12.01343,12.01343,0,0,1,56,128ZM44,180a12,12,0,1,0,12,12A12.01343,12.01343,0,0,0,44,180ZM44,52A12,12,0,1,0,56,64,12.01343,12.01343,0,0,0,44,52ZM88,72H216a8,8,0,0,0,0-16H88a8,8,0,0,0,0,16Zm128,48H88.00586a8,8,0,1,0,0,16H216a8,8,0,0,0,0-16Zm0,64H88.00586a8,8,0,1,0,0,16H216a8,8,0,0,0,0-16Z" />
        </svg>
      </button>

      <button
        className={styles['custom-icon-button']}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
      >
        <svg
          fill="#000000"
          width="800px"
          height="800px"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3.59 3.03h12.2v1.26H3.59zm0 4.29h12.2v1.26H3.59zm0 4.35h12.2v1.26H3.59zM.99 4.79h.49V2.52H.6v.45h.39v1.82zm.87 3.88H.91l.14-.11.3-.24c.35-.28.49-.5.49-.79A.74.74 0 0 0 1 6.8a.77.77 0 0 0-.81.84h.52A.34.34 0 0 1 1 7.25a.31.31 0 0 1 .31.31.6.6 0 0 1-.22.44l-.87.75v.39h1.64zm-.36 3.56a.52.52 0 0 0 .28-.48.67.67 0 0 0-.78-.62.71.71 0 0 0-.77.75h.5a.3.3 0 0 1 .27-.32.26.26 0 1 1 0 .51H.91v.38H1c.23 0 .37.11.37.29a.29.29 0 0 1-.33.29.35.35 0 0 1-.36-.35H.21a.76.76 0 0 0 .83.8.74.74 0 0 0 .83-.72.53.53 0 0 0-.37-.53z" />
        </svg>
      </button>

      <button
        className={styles['custom-icon-button']}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={editor.isActive('blockquote') ? 'is-active' : ''}
      >
        <svg
          width="12"
          height="10"
          viewBox="0 0 12 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1.65333 0.670754C2.32652 0.452272 3.10254 0.45618 3.73782 0.782615C5.20338 1.53569 5.83747 3.47667 5.24756 5.5333C4.98294 6.45586 4.5175 7.35167 3.53181 8.25584C2.54611 9.16 1.17899 9.69334 0.544721 9.69334C0.3175 9.69334 0.133301 9.51425 0.133301 9.29334C0.133301 9.07242 0.321503 8.89334 0.544721 8.89334C1.26927 8.89334 1.91665 8.49903 2.68325 7.91834C3.26999 7.47388 3.72812 6.90539 3.9818 6.41C4.46698 5.46255 4.48339 4.12715 3.69948 3.77224C3.32303 4.18349 2.83142 4.42339 2.24237 4.42339C0.962419 4.42339 0.208069 3.39369 0.240507 2.46139C0.271509 1.57036 0.837647 0.935479 1.65333 0.670754ZM8.05333 0.670754C8.72652 0.452272 9.50254 0.45618 10.1378 0.782615C11.6034 1.53569 12.2375 3.47667 11.6476 5.5333C11.3829 6.45586 10.9175 7.35167 9.93181 8.25584C8.94611 9.16 7.57899 9.69334 6.94472 9.69334C6.7175 9.69334 6.5333 9.51425 6.5333 9.29334C6.5333 9.07242 6.7215 8.89334 6.94472 8.89334C7.66927 8.89334 8.31665 8.49903 9.08325 7.91834C9.66999 7.47388 10.1281 6.90539 10.3818 6.41C10.867 5.46255 10.8834 4.12715 10.0995 3.77224C9.72303 4.18349 9.23142 4.42339 8.64237 4.42339C7.36242 4.42339 6.60807 3.39369 6.64051 2.46139C6.67151 1.57036 7.23765 0.935479 8.05333 0.670754Z"
            fill="#7A8296"
          />
        </svg>
      </button>

      <DropdownMenu
        items={[
          {
            title: 'Paragraph',
            onSelect: () => editor.chain().focus().setParagraph().run(),
            disabled: editor.isActive('paragraph'),
          },
          {
            title: 'Heading 1',
            onSelect: () => handleHeadingSelect(1),
            disabled: editor.isActive('heading', { level: 1 }),
          },
          {
            title: 'Heading 2',
            onSelect: () => handleHeadingSelect(2),
            disabled: editor.isActive('heading', { level: 2 }),
          },
          {
            title: 'Heading 3',
            onSelect: () => handleHeadingSelect(3),
            disabled: editor.isActive('heading', { level: 3 }),
          },
          {
            title: 'Heading 4',
            onSelect: () => handleHeadingSelect(4),
            disabled: editor.isActive('heading', { level: 4 }),
          },
          {
            title: 'Heading 5',
            onSelect: () => handleHeadingSelect(5),
            disabled: editor.isActive('heading', { level: 5 }),
          },
          {
            title: 'Heading 6',
            onSelect: () => handleHeadingSelect(6),
            disabled: editor.isActive('heading', { level: 6 }),
          },
        ]}
        trigger={
          <button className={styles['custom-icon-button']} style={{ width: '3.2rem' }}>
            <div className={styles['drop-down-trigger']}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.33792 0.216797C7.08939 0.216797 6.88792 0.418269 6.88792 0.666797C6.88792 0.915325 7.08939 1.1168 7.33792 1.1168H8.64994V5.54996H3.34994V1.1168H4.67125C4.91978 1.1168 5.12125 0.915325 5.12125 0.666797C5.12125 0.418269 4.91978 0.216797 4.67125 0.216797H0.937915C0.689387 0.216797 0.487915 0.418269 0.487915 0.666797C0.487915 0.915325 0.689387 1.1168 0.937915 1.1168H2.24994V10.8835H0.937915C0.689387 10.8835 0.487915 11.0849 0.487915 11.3335C0.487915 11.582 0.689387 11.7835 0.937915 11.7835H4.67125C4.91978 11.7835 5.12125 11.582 5.12125 11.3335C5.12125 11.0849 4.91978 10.8835 4.67125 10.8835H3.34994V6.44996H8.64994V10.8835H7.33792C7.08939 10.8835 6.88792 11.0849 6.88792 11.3335C6.88792 11.582 7.08939 11.7835 7.33792 11.7835H11.0712C11.3198 11.7835 11.5212 11.582 11.5212 11.3335C11.5212 11.0849 11.3198 10.8835 11.0712 10.8835H9.74994V1.1168H11.0712C11.3198 1.1168 11.5212 0.915325 11.5212 0.666797C11.5212 0.418269 11.3198 0.216797 11.0712 0.216797H7.33792Z"
                  fill="#7A8296"
                />
              </svg>
              <div className={styles['arrow-down-wrapper']}>
                <ArrowDown />
              </div>
            </div>
          </button>
        }
      />
    </div>
  );
};
